import React, { useState, useEffect } from 'react';
import { Upload, Save, X, Image as ImageIcon, Type, Trash2, CheckCircle, AlertCircle, Layout, Globe } from 'lucide-react';
import { databaseService, storageService, appwriteConfig, ID } from '../../../lib/appwrite';
import { loadCustomFont, removeCustomFont } from '../../../utils/fontLoader';
import { GOOGLE_FONTS } from '../../../utils/googleFonts';

export default function Settings() {
  // --- Site Settings State ---
  const [siteTitle, setSiteTitle] = useState('');
  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // --- Font Management State ---
  const [customFonts, setCustomFonts] = useState([]);
  const [loadingFonts, setLoadingFonts] = useState(false);
  const [uploadingFont, setUploadingFont] = useState(false);
  const [fontUploadForm, setFontUploadForm] = useState({
    fontName: '',
    file: null,
    preview: null
  });

  const SETTINGS_COLLECTION = 'site_settings';
  const STORAGE_BUCKET = appwriteConfig.storageId || 'reactbucket';
  const FONTS_BUCKET_ID = 'custom-fonts';

  useEffect(() => {
    loadSettings();
    loadCustomFonts();
  }, []);

  // ==================== Site Settings Logic ====================

  const loadSettings = async () => {
    try {
      setLoadingSettings(true);
      const result = await databaseService.listDocuments(SETTINGS_COLLECTION);
      if (result.success && result.data.documents.length > 0) {
        const settings = result.data.documents[0];
        if (settings.siteTitle) setSiteTitle(settings.siteTitle);
        if (settings.faviconUrl) {
          setFaviconUrl(settings.faviconUrl);
          setFaviconPreview(settings.faviconUrl);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      setFaviconFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setFaviconPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadFavicon = async () => {
    if (!faviconFile) return null;
    try {
      const fileId = ID.unique();
      const result = await storageService.uploadFile(STORAGE_BUCKET, fileId, faviconFile);
      if (result.success) {
        return storageService.getFileView(STORAGE_BUCKET, result.data.$id);
      }
      return null;
    } catch (error) {
      console.error('Error uploading favicon:', error);
      return null;
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      let finalFaviconUrl = faviconUrl;

      if (faviconFile) {
        finalFaviconUrl = await uploadFavicon();
        if (!finalFaviconUrl) {
          alert('Failed to upload favicon.');
          setSavingSettings(false);
          return;
        }
      }

      const settingsData = {
        siteTitle: siteTitle || 'Parvej Hossain - Portfolio',
        faviconUrl: finalFaviconUrl || '',
      };

      const existing = await databaseService.listDocuments(SETTINGS_COLLECTION);
      if (existing.success && existing.data.documents.length > 0) {
        await databaseService.updateDocument(
          SETTINGS_COLLECTION,
          existing.data.documents[0].$id,
          settingsData
        );
      } else {
        await databaseService.createDocument(SETTINGS_COLLECTION, settingsData);
      }

      setFaviconUrl(finalFaviconUrl);
      setFaviconFile(null);
      if (siteTitle) document.title = siteTitle;
      if (finalFaviconUrl) updateFaviconInDOM(finalFaviconUrl);

      alert('Site settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const updateFaviconInDOM = (url) => {
    if (!url) return;
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    existingLinks.forEach(link => link.remove());
    
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = `${url}?t=${Date.now()}`;
    document.head.appendChild(link);
  };

  // ==================== Font Management Logic ====================

  const loadCustomFonts = async () => {
    try {
      setLoadingFonts(true);
      const result = await databaseService.listDocuments('custom_fonts');
      if (result.success) {
        setCustomFonts(result.data.documents);
        result.data.documents.forEach(font => {
          loadCustomFont(font.fontName, font.fileUrl);
        });
      }
    } catch (error) {
      console.error('Error loading fonts:', error);
    } finally {
      setLoadingFonts(false);
    }
  };

  const handleFontFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.ttf')) {
      alert('Please upload a .ttf file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }
    setFontUploadForm({ ...fontUploadForm, file, preview: file.name });
  };

  const handleUploadFont = async () => {
    if (!fontUploadForm.fontName.trim() || !fontUploadForm.file) {
      alert('Please provide both font name and file');
      return;
    }

    try {
      setUploadingFont(true);
      const fileId = ID.unique();
      const uploadResult = await storageService.uploadFile(FONTS_BUCKET_ID, fileId, fontUploadForm.file);
      
      if (!uploadResult.success) throw new Error('Failed to upload font file');

      await storageService.setFilePublic(FONTS_BUCKET_ID, uploadResult.data.$id);
      const fileUrl = storageService.getFileView(FONTS_BUCKET_ID, uploadResult.data.$id);

      const fontData = {
        fontName: fontUploadForm.fontName.trim(),
        fileName: fontUploadForm.file.name,
        fileUrl: fileUrl,
        fileId: uploadResult.data.$id,
        fileSize: fontUploadForm.file.size,
        uploadedAt: new Date().toISOString()
      };

      const dbResult = await databaseService.createDocument('custom_fonts', fontData);
      if (!dbResult.success) {
         await storageService.deleteFile(FONTS_BUCKET_ID, uploadResult.data.$id);
         throw new Error('Failed to save font metadata');
      }

      loadCustomFont(fontData.fontName, fileUrl);
      loadCustomFonts(); // Reload list
      setFontUploadForm({ fontName: '', file: null, preview: null });
      alert('Font uploaded successfully!');
    } catch (error) {
      console.error('Error uploading font:', error);
      alert('Error uploading font');
    } finally {
      setUploadingFont(false);
    }
  };

  const handleDeleteFont = async (font) => {
    if (!confirm(`Delete "${font.fontName}"? This cannot be undone.`)) return;
    try {
      await databaseService.deleteDocument('custom_fonts', font.$id);
      if (font.fileId) await storageService.deleteFile(FONTS_BUCKET_ID, font.fileId);
      removeCustomFont(font.fontName);
      loadCustomFonts();
    } catch (error) {
      console.error('Error deleting font:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#4C1D95]">Admin Settings</h1>
        <p className="text-gray-500 mt-1">Manage global site configuration and styling assets.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        
        {/* === Left Column: Site Identity === */}
        <div className="space-y-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
              <Globe className="w-5 h-5 text-[#4C1D95]" />
              <h2 className="text-lg font-bold text-gray-800">Site Identity</h2>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Site Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Site Title
                </label>
                <input
                  type="text"
                  value={siteTitle}
                  onChange={(e) => setSiteTitle(e.target.value)}
                  placeholder="e.g. Parvej Hossain - Portfolio"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                />
              </div>

              {/* Favicon */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Site Favicon
                </label>
                <div className="flex items-start gap-6">
                  <div className="relative group">
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-gray-50 overflow-hidden">
                      {faviconPreview ? (
                        <img src={faviconPreview} alt="Favicon" className="w-full h-full object-contain p-2" />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-gray-300" />
                      )}
                    </div>
                    {faviconFile && (
                      <button 
                        onClick={() => { setFaviconFile(null); setFaviconPreview(faviconUrl); }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-sm hover:bg-red-600 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <label className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-[#4C1D95] hover:text-[#4C1D95] transition-all w-full sm:w-auto">
                      <Upload size={16} />
                      <span className="text-sm font-medium">{faviconFile ? 'Change File' : 'Upload New'}</span>
                      <input type="file" accept="image/*" onChange={handleFaviconChange} className="hidden" />
                    </label>
                    <p className="mt-2 text-xs text-gray-500 leading-relaxed">
                      Recommended size: 32x32px. <br/>
                      Supports PNG, ICO, SVG, WEBP.
                    </p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-4 flex justify-end">
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#4C1D95] text-white rounded-lg font-medium hover:bg-[#3b1575] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow"
                >
                  <Save size={18} />
                  {savingSettings ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>

          {/* === Available Google Fonts (Quick View) === */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
             <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
              <Layout className="w-5 h-5 text-[#4C1D95]" />
              <h2 className="text-lg font-bold text-gray-800">Available System Fonts</h2>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3 max-h-[300px] overflow-y-auto custom-scrollbar">
               {GOOGLE_FONTS.slice(0, 8).map((font, idx) => (
                 <div key={idx} className="p-3 border border-gray-100 rounded-lg hover:border-purple-100 hover:bg-purple-50/30 transition-colors">
                   <div className="flex items-center justify-between mb-1">
                     <span className="text-xs font-semibold text-gray-600">{font.name}</span>
                     {font.bangla && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Bangla</span>}
                   </div>
                   <p className="text-sm text-gray-800 truncate" style={{fontFamily: font.value}}>The quick brown fox</p>
                 </div>
               ))}
               <div className="col-span-2 text-center pt-2">
                 <p className="text-xs text-gray-400">And {GOOGLE_FONTS.length - 8} more available in editor...</p>
               </div>
            </div>
          </div>
        </div>

        {/* === Right Column: Font Management === */}
        <div className="space-y-8">
           <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full">
            <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Type className="w-5 h-5 text-[#4C1D95]" />
                <h2 className="text-lg font-bold text-gray-800">Custom Fonts</h2>
              </div>
              <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-1 rounded-full">{customFonts.length} Uploaded</span>
            </div>

            <div className="p-6 flex-1 flex flex-col gap-6">
              {/* Upload Section */}
              <div className="bg-purple-50/50 rounded-xl p-5 border border-purple-100">
                <h3 className="text-sm font-bold text-gray-800 mb-4">Upload New Font</h3>
                <div className="space-y-4">
                  <div>
                    <input
                      type="text"
                      value={fontUploadForm.fontName}
                      onChange={(e) => setFontUploadForm({...fontUploadForm, fontName: e.target.value})}
                      placeholder="Font Name (e.g. My Custom Font)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none bg-white"
                    />
                  </div>
                  <div className="flex gap-3">
                     <label className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:border-[#4C1D95] hover:text-[#4C1D95] transition-all text-sm">
                        <Upload size={16} />
                        <span className="truncate">{fontUploadForm.preview || 'Choose .ttf File'}</span>
                        <input type="file" accept=".ttf" onChange={handleFontFileSelect} className="hidden" />
                     </label>
                     <button 
                        onClick={handleUploadFont}
                        disabled={uploadingFont || !fontUploadForm.file || !fontUploadForm.fontName}
                        className="px-4 py-2 bg-[#4C1D95] text-white rounded-lg text-sm font-medium hover:bg-[#3b1575] disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       {uploadingFont ? '...' : 'Upload'}
                     </button>
                  </div>
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-500">Only .ttf files allowed (Max 5MB). Ensure font supports required character sets (Bangla/English).</p>
                  </div>
                </div>
              </div>

              {/* Font List */}
              <div className="flex-1">
                 <h3 className="text-sm font-bold text-gray-800 mb-4">Installed Fonts</h3>
                 {loadingFonts ? (
                    <div className="flex justify-center py-8 text-gray-400 text-sm">Loading fonts...</div>
                 ) : customFonts.length > 0 ? (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-1">
                      {customFonts.map((font) => (
                        <div key={font.$id} className="p-4 border border-gray-200 rounded-lg hover:border-purple-200 transition-all bg-white group">
                           <div className="flex justify-between items-start mb-2">
                             <div>
                               <div className="flex items-center gap-2">
                                 <h4 className="font-bold text-gray-800 text-sm">{font.fontName}</h4>
                                 <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200">Custom</span>
                               </div>
                               <p className="text-xs text-gray-400 mt-0.5">{font.fileName} • {formatFileSize(font.fileSize)}</p>
                             </div>
                             <button 
                               onClick={() => handleDeleteFont(font)}
                               className="text-gray-400 hover:text-red-500 transition-colors p-1"
                               title="Delete"
                             >
                               <Trash2 size={16} />
                             </button>
                           </div>
                           <div className="p-3 bg-gray-50 rounded border border-gray-100 text-sm overflow-hidden" style={{fontFamily: `'${font.fontName}', sans-serif`}}>
                             The quick brown fox jumps over the lazy dog.
                           </div>
                        </div>
                      ))}
                    </div>
                 ) : (
                    <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                      <Type className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No custom fonts installed</p>
                    </div>
                 )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
}


